import { patchItemSupplier, postItemSupplier } from '@/lib/api/inventory.js';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState, type ReactElement } from 'react';
import { str } from './item-form-utils.js';

type SupplierRow = {
  id?: unknown;
  supplierName?: unknown;
  supplierId?: unknown;
  supplierSku?: unknown;
  costPrice?: unknown;
  preferred?: unknown;
};

type Props = {
  itemId: string;
  suppliers: SupplierRow[];
};

export function SuppliersSection({ itemId, suppliers }: Props): ReactElement {
  const queryClient = useQueryClient();
  const [supplierId, setSupplierId] = useState('');
  const [supplierSku, setSupplierSku] = useState('');
  const [supplierCost, setSupplierCost] = useState('0');
  const [supplierPreferred, setSupplierPreferred] = useState(true);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);
  const [editSupplierSku, setEditSupplierSku] = useState('');
  const [editSupplierCost, setEditSupplierCost] = useState('0');
  const [editSupplierPreferred, setEditSupplierPreferred] = useState(false);

  const addSupplierMutation = useMutation({
    mutationFn: (body: {
      supplierId: string;
      costPrice: number;
      supplierSku?: string;
      preferred?: boolean;
    }) => {
      if (!itemId) throw new Error('Item id is required');
      return postItemSupplier(itemId, body);
    },
    onSuccess: () => {
      toastSuccess('Supplier linked');
      setSupplierId('');
      setSupplierSku('');
      setSupplierCost('0');
      setSupplierPreferred(true);
      void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const patchSupplierMutation = useMutation({
    mutationFn: (args: {
      linkId: string;
      body: { supplierSku?: string | null; costPrice?: number; preferred?: boolean };
    }) => {
      if (!itemId) throw new Error('Item id is required');
      return patchItemSupplier(itemId, args.linkId, args.body);
    },
    onSuccess: () => {
      toastSuccess('Supplier updated');
      setEditingSupplierId(null);
      void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <section id="section-suppliers" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-slate-950">Suppliers</h2>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="py-2">Supplier</th>
            <th className="py-2">SKU</th>
            <th className="py-2">Cost</th>
            <th className="py-2">Preferred</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {suppliers.map((s) => {
            const linkId = String(s.id);
            const isEditing = editingSupplierId === linkId;
            return (
              <tr key={linkId}>
                <td className="py-2">{String(s.supplierName ?? s.supplierId)}</td>
                <td className="py-2">
                  {isEditing ? (
                    <input
                      className="w-36 rounded-lg border px-2 py-1 text-sm"
                      value={editSupplierSku}
                      onChange={(e) => setEditSupplierSku(e.target.value)}
                      placeholder="Supplier SKU"
                    />
                  ) : (
                    String(s.supplierSku ?? '—')
                  )}
                </td>
                <td className="py-2">
                  {isEditing ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-24 rounded-lg border px-2 py-1 text-sm"
                      value={editSupplierCost}
                      onChange={(e) => setEditSupplierCost(e.target.value)}
                    />
                  ) : (
                    formatCurrency(Number(s.costPrice ?? 0))
                  )}
                </td>
                <td className="py-2">
                  {isEditing ? (
                    <label className="inline-flex items-center gap-1 text-xs">
                      <input
                        type="checkbox"
                        checked={editSupplierPreferred}
                        onChange={(e) => setEditSupplierPreferred(e.target.checked)}
                      />
                      Preferred
                    </label>
                  ) : s.preferred ? (
                    'Yes'
                  ) : (
                    ''
                  )}
                </td>
                <td className="py-2 text-right">
                  {isEditing ? (
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                        onClick={() => setEditingSupplierId(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-primary"
                        disabled={patchSupplierMutation.isPending}
                        onClick={() =>
                          patchSupplierMutation.mutate({
                            linkId,
                            body: {
                              supplierSku: editSupplierSku.trim() || null,
                              costPrice: Number(editSupplierCost) || 0,
                              preferred: editSupplierPreferred,
                            },
                          })
                        }
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="text-xs font-semibold text-slate-600 hover:text-slate-950"
                      onClick={() => {
                        setEditingSupplierId(linkId);
                        setEditSupplierSku(str(s.supplierSku));
                        setEditSupplierCost(str(s.costPrice, '0'));
                        setEditSupplierPreferred(Boolean(s.preferred));
                      }}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {suppliers.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">No suppliers linked yet.</p>
      ) : null}
      <form
        className="mt-4 flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!supplierId) return;
          const body: {
            supplierId: string;
            costPrice: number;
            supplierSku?: string;
            preferred?: boolean;
          } = {
            supplierId,
            costPrice: Number(supplierCost) || 0,
            preferred: supplierPreferred,
          };
          if (supplierSku.trim()) body.supplierSku = supplierSku.trim();
          addSupplierMutation.mutate(body);
        }}
      >
        <label className="block min-w-[12rem] flex-1">
          <span className="text-xs font-medium text-slate-600">Add supplier</span>
          <div className="mt-1">
            <SupplierSelect value={supplierId} onChange={setSupplierId} required />
          </div>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Supplier SKU</span>
          <input
            className="mt-1 block w-40 rounded-xl border px-3 py-2 text-sm"
            value={supplierSku}
            onChange={(e) => setSupplierSku(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Cost</span>
          <input
            type="number"
            step="0.01"
            min="0"
            className="mt-1 block w-28 rounded-xl border px-3 py-2 text-sm"
            value={supplierCost}
            onChange={(e) => setSupplierCost(e.target.value)}
          />
        </label>
        <label className="mb-2 flex items-center gap-2 text-xs font-medium text-slate-600">
          <input
            type="checkbox"
            checked={supplierPreferred}
            onChange={(e) => setSupplierPreferred(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          Preferred
        </label>
        <button
          type="submit"
          disabled={addSupplierMutation.isPending || !supplierId}
          className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {addSupplierMutation.isPending ? 'Linking…' : 'Link'}
        </button>
      </form>
    </section>
  );
}
